import isEqualWith from 'lodash/isEqualWith'
import pick from 'lodash/pick'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'

import { CompanyValues } from '@features/invoicingSettings/types/companyValues'

import { useFormContext, useWatch } from '../../../../../../../../hooks'
import { InvoiceSettingsForm } from '../../types/invoiceSettingsForm'
import { InvoiceSettingsFormSchema } from '../../types/invoiceSettingsFormSchema'
import { invoiceSettingsFormFieldsToDebounce } from './constants/invoiceSettingsFormFieldsToDebounce'
import { invoiceSettingsFormIgnoredLivePreviewFields } from './constants/invoiceSettingsFormIgnoredLivePreviewFields'
import { InvoiceSettingsHtmlPreview } from './elements'
import { useDebouncedInvoiceSettingsFormValues } from './hooks/useDebouncedInvoiceSettingsFormValues'
import { useInvoiceSettingsPreview } from './hooks/useInvoiceSettingsPreview'
import { useInvoiceSettingsPreviewPayload } from './hooks/useInvoiceSettingsPreviewPayload'
import * as Styled from './styles'

export const InvoiceSettingsPreview = (): ReactElement => {
  const [htmlFiles, setHtmlFiles] = useState<string[] | undefined>(undefined)
  const { zervantSettings, isLoading: isLoadingPreviewPayload } = useInvoiceSettingsPreviewPayload()
  const { control } = useFormContext<InvoiceSettingsFormSchema>()
  const previewWrapperRef = useRef<HTMLDivElement>(null)
  const previousLivePreviewFieldsRef = useRef<Partial<InvoiceSettingsForm>>({})
  const previousLegalSettingsRef = useRef<Partial<CompanyValues>>({})

  const invoiceSettingsFormValues = useWatch({ control }) as InvoiceSettingsForm

  const debouncedInvoiceSettingsFormValues = useDebouncedInvoiceSettingsFormValues(
    invoiceSettingsFormValues,
    invoiceSettingsFormFieldsToDebounce,
  )

  // These are the fields that actually affect the live HTML preview, ignored fields are filtered out via ignoredLivePreviewFields
  const livePreviewTriggerFieldKeys = useMemo(() => {
    return Object.keys(debouncedInvoiceSettingsFormValues).filter(
      (fieldKey) => !invoiceSettingsFormIgnoredLivePreviewFields.includes(fieldKey as keyof InvoiceSettingsForm),
    )
  }, [debouncedInvoiceSettingsFormValues])

  const { getInvoiceSettingsPreview, isLoading: isLoadingInvoiceSettingsPreview } = useInvoiceSettingsPreview({
    onSuccess: ({ data }) => {
      setHtmlFiles(data)
    },
  })

  useEffect(() => {
    const fieldsAffectingLivePreview = pick(debouncedInvoiceSettingsFormValues, livePreviewTriggerFieldKeys)
    const haveRelevantFieldsChanged = !isEqualWith(fieldsAffectingLivePreview, previousLivePreviewFieldsRef.current)
    const legalSettings = pick(zervantSettings?.values?.company, ['legalFormSettings', 'legalMentions'])
    const haveLegalSettingsChanged = !isEqualWith(legalSettings, previousLegalSettingsRef.current)

    if (!isLoadingPreviewPayload && (haveRelevantFieldsChanged || haveLegalSettingsChanged)) {
      getInvoiceSettingsPreview(debouncedInvoiceSettingsFormValues)
      previousLivePreviewFieldsRef.current = fieldsAffectingLivePreview
      previousLegalSettingsRef.current = legalSettings
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInvoiceSettingsFormValues, isLoadingPreviewPayload, zervantSettings?.values?.company])

  return (
    <Styled.PreviewWrapper ref={previewWrapperRef}>
      <InvoiceSettingsHtmlPreview
        htmlFiles={htmlFiles}
        isLoading={isLoadingPreviewPayload || isLoadingInvoiceSettingsPreview}
        previewWrapperRef={previewWrapperRef}
      />
    </Styled.PreviewWrapper>
  )
}
